import { useState, useEffect } from 'react'
import {
  Box, Grid, Card, CardContent, CardActions, Typography, Button, Chip,
  ToggleButton, ToggleButtonGroup, CircularProgress, Alert,
} from '@mui/material'
import { Check, Close } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { billingApi, tenantApi } from '../services/api'
import { RootState } from '../store'
import EnterpriseMatrix from '../components/EnterpriseMatrix'

const PricingPage = () => {
  const [plans, setPlans] = useState<any[]>([])
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly')
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [tenant, setTenant] = useState<any>(null)
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id

  useEffect(() => {
    const fetch = async () => {
      try {
        const [plansRes, tenantRes] = await Promise.allSettled([
          billingApi.getPlans(),
          tenantId ? tenantApi.getTenant(tenantId) : Promise.resolve({ data: null }),
        ])
        if (plansRes.status === 'fulfilled') {
          // Filter out non-public plans and sort by sort_order (or price as fallback)
          const allPlans = plansRes.value.data || []
          setPlans(allPlans.filter((p: any) => p.is_public !== false).sort((a: any, b: any) => (a.sort_order ?? a.base_price_monthly) - (b.sort_order ?? b.base_price_monthly)))
        }
        if (tenantRes.status === 'fulfilled') setTenant(tenantRes.value.data)
      } catch {}
      setLoading(false)
    }
    fetch()
  }, [tenantId])

  const handleUpgrade = async (plan: any) => {
    if (!tenantId) return
    setUpgrading(plan.id)
    setError('')
    setSuccess('')
    try {
      await billingApi.createUpgradeRequest({
        tenant_id: tenantId,
        plan_id: plan.name || plan.id,
        billing_cycle: billingCycle,
        tenant_name: tenant?.name || '',
        billing_email: tenant?.billing_email || user?.email || '',
      })
      setSuccess('Thank you! Our team will reach out shortly with a secure payment link.')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Request failed. Please try again.')
    }
    setUpgrading(null)
  }

  const currentTier = tenant?.tier

  const featureList = (plan: any) => {
    const features = plan.features || {}
    // Agents read "up to N", because N is a ceiling rather than an allocation
    // and Enterprise is one plan whose band — 2K, 5K or 10K — is set per
    // contract. "10,000 agents" beside a banded tier reads as what you get;
    // "up to 10,000" reads as what the tier tops out at, which is the claim.
    const agentCeiling = (plan.included_agents == null || plan.included_agents >= 999999)
      ? null : plan.included_agents
    const agentLabel = agentCeiling == null
      ? 'Unlimited agents'
      : `Up to ${agentCeiling.toLocaleString()} agents`
    const repos = (plan.included_repositories == null || plan.included_repositories >= 999999) ? 'Unlimited' : plan.included_repositories
    const scans = (plan.included_scans_monthly == null || plan.included_scans_monthly >= 999999) ? 'Unlimited' : plan.included_scans_monthly
    const users = (plan.included_users == null || plan.included_users >= 999999) ? 'Unlimited' : plan.included_users
    return [
      { label: agentLabel, included: true },
      { label: `${repos} repositories`, included: true },
      { label: `${scans} scans/month`, included: true },
      { label: `${users} users`, included: true },
      { label: `${plan.data_retention_days} day retention`, included: true },
      { label: 'CI/CD gate', included: features.cicd_gate !== false },
      { label: 'API & data export', included: features.api_access !== false },
      ...(() => {
        // Compliance Posture state derives from the plan tier
        const cp = features.compliance_posture
        if (cp === 'included') return [{ label: 'Compliance Posture (all frameworks)', included: true }]
        if (cp === 'addon')    return [{ label: 'Compliance Posture — $499/mo add-on', included: true }]
        if (cp === 'teaser')   return [{ label: 'Compliance Posture (trial preview)', included: true }]
        // not_included or older plans without the field
        return [{ label: 'Compliance Posture', included: false }]
      })(),
      { label: 'ABF history & drift velocity', included: features.abf_history === true },
      { label: `Custom KB rules (${plan.max_custom_rules || 0})`, included: features.custom_rules === true },
      ...(plan.type === 'enterprise' ? [
        { label: 'Change Impact Analysis', included: true },
        { label: 'Webhook integration', included: true },
        { label: 'SSO / SAML', included: true },
      ] : []),
      { label: features.named_csm ? 'Named CSM + SLA' : features.priority_support ? 'Priority support' : 'Email support', included: true },
    ]
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>Choose Your Plan</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Pick the tier that matches your fleet. Talk to us before you commit.
        </Typography>
        <ToggleButtonGroup
          value={billingCycle}
          exclusive
          onChange={(_, v) => v && setBillingCycle(v)}
          size="small"
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="annual">Annual (save ~17%)</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3} justifyContent="center">
        {plans.map((plan) => {
          const price = billingCycle === 'monthly' ? plan.base_price_monthly : plan.base_price_annual
          const isCurrent = currentTier === plan.type
          const isDowngrade = false // simplified — actual logic would compare tier levels

          return (
            <Grid item xs={12} sm={6} md={3} key={plan.id}>
              <Card sx={{
                height: '100%', display: 'flex', flexDirection: 'column',
                border: isCurrent ? '2px solid' : '1px solid',
                borderColor: isCurrent ? 'primary.main' : 'divider',
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">{plan.display_name}</Typography>
                    {isCurrent && <Chip label="Current" color="primary" size="small" />}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {plan.description}
                  </Typography>
                  <Typography variant="h4" sx={{ mb: 0.5 }}>
                    {plan.type === 'enterprise' ? 'Custom' : Number(price) === 0 ? 'Free' : `$${Number(price).toLocaleString()}`}
                  </Typography>
                  {plan.type === 'enterprise' ? (
                    <Typography variant="caption" color="text.secondary">
                      tailored to your scale
                    </Typography>
                  ) : Number(price) > 0 && billingCycle === 'annual' ? (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        /year &mdash; ${Math.round(Number(price) / 12).toLocaleString()}/mo
                      </Typography>
                      {plan.base_price_monthly > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 600 }}>
                          Save ${(plan.base_price_monthly * 12 - Number(price)).toLocaleString()}/year
                        </Typography>
                      )}
                    </>
                  ) : Number(price) > 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      /month
                    </Typography>
                  ) : null}
                  <Box sx={{ mt: 2 }}>
                    {featureList(plan).map((f, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.3 }}>
                        {f.included
                          ? <Check sx={{ fontSize: 16, color: 'success.main' }} />
                          : <Close sx={{ fontSize: 16, color: 'text.disabled' }} />
                        }
                        <Typography variant="body2" color={f.included ? 'text.primary' : 'text.disabled'}>
                          {f.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  {isCurrent ? (
                    <Button fullWidth disabled variant="outlined">Current Plan</Button>
                  ) : plan.type === 'trial' ? (
                    <Button fullWidth disabled variant="outlined">Trial</Button>
                  ) : plan.type === 'enterprise' ? (
                    <Button fullWidth variant="outlined" href="mailto:sales@tryarias.com">
                      Contact Sales
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={upgrading !== null}
                      onClick={() => handleUpgrade(plan)}
                    >
                      {upgrading === plan.id ? <CircularProgress size={20} /> : 'Upgrade'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
        Every plan starts with a demo against your own repositories, so you can see
        what ARIAS finds before you choose one.
      </Typography>

      {/* Enterprise is two choices, not one card reading "Custom": the agent
          band and the bundles. Shown as a matrix with no figures, because the
          two resolve into one contract and a number in any cell would be a
          quote nobody authorised — and the cheapest cell would become the
          anchor for every conversation. */}
      <EnterpriseMatrix />

      <Box sx={{ mt: 6, p: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2, maxWidth: 720, mx: 'auto' }}>
        <Typography variant="overline" color="text.secondary">Add-on</Typography>
        <Typography variant="h6" gutterBottom>Compliance Posture — $499/mo</Typography>
        <Typography variant="body2" color="text.secondary">
          Detect, track, and report how your agents are aligned to EU AI Act, NIST AI RMF, ISO 42001,
          SOC 2, GDPR, OWASP LLM/Agentic Top 10, and MAESTRO. Part of ARIAS Evidence on
          Enterprise; available as a self-serve add-on on Business plans.
        </Typography>
      </Box>
    </Box>
  )
}

export default PricingPage
